// client/src/store/SocketContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { Context } from "../index";

const SocketContext = createContext(null);
export const useSocket = () => useContext(SocketContext);

const normalizeUrl = (url) => String(url || "").replace(/\/+$/, "");

// helper: подождать idle/тик — чтобы не мешать первому рендеру
function scheduleAfterPaint(cb) {
    if (typeof window === "undefined") return;
    if ("requestIdleCallback" in window) {
        const id = window.requestIdleCallback(() => cb(), { timeout: 1500 });
        return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(cb, 300);
    return () => clearTimeout(t);
}

export const SocketProvider = ({ children }) => {
    const { user } = useContext(Context);
    const [socket, setSocket] = useState(null);

    useEffect(() => {
        let isCancelled = false;
        let disconnectScheduler = null;
        let newSocket = null;

        const SOCKET_URL =
            normalizeUrl(process.env.REACT_APP_API_URL) || "http://localhost:5000";

        const getToken = () => localStorage.getItem("token");

        // ⬇️ ВАЖНО: динамический импорт, чтобы socket.io-client не попал в main
        const connect = async () => {
            console.log("🌐 Подключаем сокет (Auth:", user.isAuth, ")");

            const mod = await import("socket.io-client");
            const io = mod.io ?? mod.default ?? mod;

            if (isCancelled) return;

            newSocket = io(SOCKET_URL, {
                withCredentials: true,
                transports: ["websocket", "polling"],
                auth: { token: getToken() },
                extraHeaders: {
                    ...(getToken() && { authorization: `Bearer ${getToken()}` }),
                },
            });

            newSocket.on("connect", () => {
                console.log("✅ SOCKET CONNECTED:", newSocket.id);
            });

            newSocket.on("connect_error", (err) => {
                console.warn("⚠️ SOCKET ERROR:", err.message);
            });

            if (!isCancelled) setSocket(newSocket);
        };

        // Подключаемся после first paint / idle (чат гостям не ломаем, просто делаем мягче старт)
        disconnectScheduler = scheduleAfterPaint(() => {
            connect().catch((e) => console.warn("⚠️ SOCKET INIT FAILED:", e?.message));
        });

        return () => {
            isCancelled = true;
            if (disconnectScheduler) disconnectScheduler();

            if (newSocket) {
                console.log("🟡 SOCKET DISCONNECTING");
                newSocket.close();
            }
            setSocket(null);
        };
    }, [user.isAuth]);

    return (
        <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
    );
};
